<script setup lang="ts">
import { useInvestmentProcessDataState } from '../../../utilities/investment-process/useInvestmentProcess'
import { navigateToNextStep } from '../../../utilities/account/useInvestmentProcessNavigation'
import { getCampaignData } from '../../../utilities/investment-process/useCampaign'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import InvestmentProcessNavigationButtons from '../InvestmentProcessNavigationButtons.vue'
import DocumentDownloadList from '../documents/DocumentDownloadList.vue'
import InvestmentSummaryTable from '../investmentAmount/InvestmentSummaryTable.vue'
import { i18n } from '../../../localization'
import { notify } from '../../../utilities/useNotifications'
import ECSPWarnings from './ECSPWarnings.vue'
import {
    isVerifiedExpert,
    showNoFinancialSituationProvidedOrInvestmentIsTooHighWarning,
    showNotEnoughExperienceWarning,
    showNotEnoughRiskAcknowledgementsWarning,
} from '../../../utilities/account/useEcspRegulatory'
import { trackAnalyticsInvestmentProcessStep3 } from '../../../utilities/useTracking'
import AcceptPublicInvestmentCheckbox from '../disclaimers/AcceptPublicInvestmentCheckbox.vue'
import DisclaimersCheckboxes from '../disclaimers/DisclaimersCheckboxes.vue'
import NyalaCustomerVaultPin from '../vault/NyalaCustomerVaultPin.vue'
import { useNyalaCustomerVaultData } from '../../../utilities/account/useNyalaCustomerVaultData'
import BrokerField from '../broker/BrokerField.vue'
import AcceptSavingsPlanCheckbox from '../savingsPlan/AcceptSavingsPlanCheckbox.vue'
import { getPlatformSettings } from '../../../utilities/usePlatformSettings'
import AcceptGiftMembershipCheckbox from '../giftMembership/AcceptGiftMembershipCheckbox.vue'

const store = useInvestmentProcessDataState()
const { contractType, acceptAllWarnings, allRisksAccepted, nyalaPassphrase, brokerNumber, brokerDisclaimer } =
    storeToRefs(store)

const acceptAllDocuments = ref(false)

onMounted(() => {
    trackAnalyticsInvestmentProcessStep3()
})

const hasWarnings = computed(() => {
    return (
        !isVerifiedExpert() &&
        (showNotEnoughExperienceWarning() ||
            showNotEnoughRiskAcknowledgementsWarning() ||
            showNoFinancialSituationProvidedOrInvestmentIsTooHighWarning())
    )
})

const orderLoading = ref(false)

async function createOrder() {
    if (getCampaignData().asset_data.broker?.broker_field_required && !brokerNumber.value) {
        notify('Error', i18n.global.t('investment_process.please_provide_broker_id'))
    } else if (getCampaignData().asset_data.contract_data_documents.length > 0 && acceptAllDocuments.value === false) {
        notify('Error', i18n.global.t('investment_process.please_accept_all_documents'))
    } else if (
        allRisksAccepted.value === false ||
        (getCampaignData().asset_data.broker?.broker_field_required &&
            getCampaignData().asset_data.broker?.broker_disclaimer &&
            !brokerDisclaimer.value)
    ) {
        notify('Error', i18n.global.t('investment_process.please_accept_all_disclaimers'))
    } else if (hasWarnings.value === true && acceptAllWarnings.value === false) {
        notify('Error', i18n.global.t('investment_process.please_accept_all_warnings'))
    } else if (
        getCampaignData().asset_data.nyala_active === true &&
        showNyalaCustomerVaultPin() &&
        useNyalaCustomerVaultData().isValidPin(nyalaPassphrase.value) === false
    ) {
        notify('Error', i18n.global.t('nyala_vault.invalid_nyala_pin'))
    } else {
        orderLoading.value = true

        const orderCreated = await store.createOrder()
        orderLoading.value = false
        if (orderCreated === true) {
            navigateToNextStep()
        }
    }
}

function isContractNyalaActive() {
    if (contractType.value === null) {
        if (getCampaignData().asset_data.contract) {
            return getCampaignData().asset_data.contract.nyala_active
        } else if (getCampaignData().asset_data.voucher_contract) {
            return getCampaignData().asset_data.voucher_contract
        }
    } else {
        return contractType.value === 'cash'
            ? getCampaignData().asset_data.contract.nyala_active
            : getCampaignData().asset_data.voucher_contract.nyala_active
    }
}

function showNyalaCustomerVaultPin(): boolean {
    return getCampaignData().asset_data.nyala_custody_provider !== 3
}
</script>

<template>
    <div class="px-4 py-6 sm:p-6 lg:p-8">
        <InvestmentSummaryTable></InvestmentSummaryTable>
        <NyalaCustomerVaultPin
            v-if="
                showNyalaCustomerVaultPin() &&
                (getCampaignData().asset_data.nyala_active === true || isContractNyalaActive())
            "></NyalaCustomerVaultPin>
        <AcceptPublicInvestmentCheckbox></AcceptPublicInvestmentCheckbox>
        <AcceptSavingsPlanCheckbox v-if="getCampaignData().asset_data.savings_plan" />
        <AcceptGiftMembershipCheckbox v-if="getPlatformSettings().gift_membership" />
        <DisclaimersCheckboxes></DisclaimersCheckboxes>

        <template v-if="getCampaignData().asset_data.contract_data_documents.length > 0">
            <h1 class="my-5 border-b pb-3 pt-4 text-2xl font-bold text-gray-700">
                {{ $t('investment_process.contract_documents') }}
            </h1>

            <div class="relative flex items-start">
                <div class="flex h-6 items-center">
                    <input
                        id="read_and_understood_all_documents"
                        v-model="acceptAllDocuments"
                        aria-describedby="comments-description"
                        name="read_and_understood_all_documents"
                        type="checkbox"
                        class="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
                </div>
                <div class="ml-3 text-sm leading-6">
                    <label for="read_and_understood_all_documents" class="font-semibold text-gray-900">{{
                        $t('investment_process.accept_all_documents')
                    }}</label>
                </div>
            </div>

            <DocumentDownloadList
                :documents="getCampaignData().asset_data.contract_data_documents"></DocumentDownloadList>
        </template>
        <BrokerField v-if="getCampaignData().asset_data.broker?.broker_status" />
        <template v-if="hasWarnings">
            <h1 class="my-5 border-b pb-3 pt-4 text-2xl font-bold text-gray-700">
                {{ $t('investment_process.investment_warnings') }}
            </h1>
            <ECSPWarnings />
        </template>

        <InvestmentProcessNavigationButtons
            :on-next-click="createOrder"
            :next-button-disabled="orderLoading"
            :next-button-text="$t('investment_process.create_order')" />
    </div>
</template>

<style scoped></style>
